import React, { useState, useEffect } from 'react';
import { X, PlayCircle, Rocket, Compass } from 'lucide-react';
import { logEvent } from 'firebase/analytics';
import { analytics } from '../../firebase/firebase';

const WalkthroughTour = ({ currentUser, isTrialActive, hasActiveNotification }) => {
  const [showInvite, setShowInvite] = useState(false);
  const [isTourOpen, setIsTourOpen] = useState(false);

  // Bumped key to '_v7' for fresh testing of the new logic
  const STORAGE_KEY = 'hasSeenTourInvite_v7';

  useEffect(() => {
    const hasSeenInvite = localStorage.getItem(STORAGE_KEY);

    // Conditions:
    // 1. User must be logged in
    // 2. Must not have seen the invite before
    // 3. Must NOT have a system notification active
    // 4. MUST BE IN TRIAL (isTrialActive === true) -> Hides it for Full Paid users
    if (currentUser && !hasSeenInvite && !hasActiveNotification && isTrialActive) {

      // 4-second delay
      const timer = setTimeout(() => {
        setShowInvite(true);
      }, 4000);

      return () => clearTimeout(timer);
    }
  }, [currentUser, isTrialActive, hasActiveNotification]);

  const handleStartTourFromInvite = () => {
    if (analytics) {
      logEvent(analytics, 'full_tour_launched', {
        source: 'auto_popup_invite',
        user_status: isTrialActive ? 'trial' : 'standard'
      });
    }
    setShowInvite(false);
    setIsTourOpen(true);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleStartTourFromButton = () => {
    if (analytics) {
      logEvent(analytics, 'full_tour_launched', {
        source: 'floating_helper_button',
        user_status: isTrialActive ? 'trial' : 'standard'
      });
    }
    setIsTourOpen(true);
  };

  const handleCloseInvite = () => {
    setShowInvite(false);
    localStorage.setItem(STORAGE_KEY, 'true');
  };

  const handleCloseTour = () => {
    setIsTourOpen(false);
  };

  if (!currentUser) return null;

  return (
    <>
      {/* --- 1. FLOATING BUTTON (Bottom Right) --- */}
      {/* Shows for EVERYONE (Trial & Full) so they can access it if they need help */}
      {!isTourOpen && (
        <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end space-y-2 group">
          <div className="absolute bottom-full mb-2 right-0 bg-slate-800 text-white text-xs px-3 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
            Watch Walkthrough
          </div>
          <button
            onClick={handleStartTourFromButton}
            className="flex items-center justify-center bg-white text-sky-600 border-2 border-sky-100 hover:border-sky-300 p-3 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-105 active:scale-95"
            aria-label="Open Product Tour"
          >
            <Compass className="h-6 w-6" />
          </button>
        </div>
      )}

      {/* --- 2. POP UP INVITATION --- */}
      {/* Only shows if user hasn't declined it yet AND is in Trial */}
      {showInvite && !isTourOpen && (
        <div className="fixed bottom-24 right-6 z-[60] animate-in slide-in-from-bottom-5 fade-in duration-500">
          <div className="bg-white p-6 rounded-2xl shadow-2xl border-2 border-sky-100 max-w-md relative overflow-hidden">
            <div className="absolute -top-10 -right-10 w-32 h-32 bg-sky-50 rounded-full opacity-50 pointer-events-none"></div>

            <button
              onClick={handleCloseInvite}
              className="absolute top-3 right-3 text-slate-300 hover:text-slate-500 transition-colors z-10"
            >
              <X size={20} />
            </button>

            <div className="flex items-start space-x-5 relative z-0">
              <div className="bg-sky-100 p-3 rounded-xl shrink-0 mt-1">
                <Rocket className="h-8 w-8 text-sky-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-bold text-slate-800 text-xl leading-tight mb-2">
                  Welcome to Mr Joel's Seller Tools!
                </h3>
                <p className="text-slate-600 text-base mb-5 leading-relaxed">
                  Discover the powerful features available in your plan. Take a <strong>quick interactive tour</strong> to optimize your TPT product listings and SEO.
                </p>

                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={handleCloseInvite}
                    className="px-4 py-2 text-sm font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-lg transition-colors"
                  >
                    Dismiss
                  </button>
                  <button
                    onClick={handleStartTourFromInvite}
                    className="px-5 py-2.5 text-sm font-bold bg-sky-600 hover:bg-sky-700 text-white rounded-lg shadow-lg hover:shadow-sky-200 transform transition-all active:scale-95 flex items-center animate-pulse-subtle"
                  >
                    <PlayCircle size={18} className="mr-2" />
                    Start Tour
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* --- 3. TOUR MODAL --- */}
      {isTourOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 animate-in fade-in duration-200" onClick={handleCloseTour}>
          {/* Changed max-w-6xl to max-w-7xl for a slightly larger view */}
          <div
            className="w-full max-w-7xl rounded-xl bg-slate-800 border border-slate-700 shadow-2xl overflow-hidden flex flex-col relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b border-slate-700 bg-slate-800">
              <h3 className="text-lg font-semibold text-sky-400 flex items-center">
                <Compass className="h-5 w-5 mr-2" />
                Platform Walkthrough
              </h3>
              <button
                onClick={handleCloseTour}
                className="text-slate-300 hover:text-white p-1 rounded-full hover:bg-slate-700 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="flex-1 relative overflow-y-auto flex items-center justify-center bg-slate-900">
              <div
                style={{
                  position: 'relative',
                  boxSizing: 'content-box',
                  maxHeight: '85vh',
                  width: '100%',
                  aspectRatio: '2.0146904512067154',
                  padding: '40px 0 40px 0',
                  background: '#0f172a'
                }}
              >
                <iframe
                  src="https://app.supademo.com/embed/cmibzew5g8mfpb7b4l3tkxyq8?embed_v=2&utm_source=embed"
                  loading="lazy"
                  title="Mr Joel's Seller Tools: The TPT Seller App Overview"
                  allow="clipboard-write"
                  frameBorder="0"
                  webkitallowfullscreen="true"
                  mozallowfullscreen="true"
                  allowFullScreen
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: '100%'
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WalkthroughTour;